import React, { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { Tooltip } from '@jutro/components';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import PropTypes from 'prop-types';
import metadata from './VehicleInfoComponent.metadata.json5';
import messages from './VehicleInfoComponent.messages';
import styles from './VehicleInfoComponent.module.scss';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react'
import {MLDriverVehicleUtil} from "ml-portals-util-react";
import config from 'app-config';
import { platformMessages } from 'ml-components-platform-react';
import { MLVehicleInfoLookupService } from 'ml-services-common-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';

function NamedNonOwnerTooltipComponent() {
    const translator = useTranslator();
    return (
        <Tooltip
            id="tooltipNamedNonOwner"
            placement="right"
            trigger="click"
            content={translator(messages.namedNonOwnerTooltipContent)}
        >
            <span className={styles.tooltipOverride}>
                <i className="mir mi-help-outline" />
            </span>
        </Tooltip>
    );
}

function VehicleInfoComponent(props) {
    const { authHeader } = useAuthentication();
    const translator = useTranslator();
    const {
        value: vehicleVM,
        submissionVM,
        id,
        quoteID,
        isVinDuplicated,
        onValueChange,
        path,
        index,
        updateVehicleDetails,
        initialPotentialContact,
        showValidationErrors,
        onValidate,
        isGpa,
        updateIsValidVin,
        removeErrorAlertIfVehiclesOwned,
        additionalInterestAvailableCandidatesML,
        updateInValidVin,
        removeInValidVin
    } = props;
    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);

    const [vehicleFinanceCategory, updateVehicleFinanceCategory] = useState([]);

    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const isModernProductPersonalAuto = isModernProduct && MLProductCodeUtil.isPersonalAuto(_.get(submissionVM, 'baseData.productCode.value'));
    const [isCostNewRequired, setIsCostNewRequired] = useState(false);
    const isQnB = config.portalCode === 'QB';

    const {
            showProgressDialog,
            hideProgressDialog
        } = MLModalService();

    const {
            createLookupCompCollRequest
        } = MLDriverVehicleUtil();

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    useEffect(() => {
        const financeCategoryValues = vehicleVM.financeCategory_ML.aspects.availableValues.map((category) => {
            return {
                code: category.code,
                name: translator({
                    id: category.name,
                    defaultMessage: category.name
                })
            };
        });
        const sortedFinanceCategoryValues = _.sortBy(financeCategoryValues, 'code');
        updateVehicleFinanceCategory(sortedFinanceCategoryValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleVM]);

    useEffect(() => {
        setTimeout(()=>{
            const element = document.getElementById("gatewayVehicleSummaryContainer");
            if(element){
                element.focus();
            }
        }, 100)
  }, []);

    const updateFinanceCategory = useCallback((value) => {
        _.set(vehicleVM, 'financeCategory_ML', value);
        if(removeErrorAlertIfVehiclesOwned){
            removeErrorAlertIfVehiclesOwned();
        }
        updateVehicleDetails();
    }, [removeErrorAlertIfVehiclesOwned, updateVehicleDetails, vehicleVM]);

    const lineVisibility = useCallback(() => {
        const additionalInterestLength = _.get(vehicleVM, 'additionalInterests.length', undefined);
        return !_.isUndefined(additionalInterestLength) && additionalInterestLength > 0;
    }, [vehicleVM]);

    const isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal = () => {
        const vehicleCategory = _.get(vehicleVM, 'category_ML.value.code', undefined);
        switch (vehicleCategory) {
            case 'trailer':
            case 'registered_golf_cart':
            case 'low_speed_vehicle':
            case 'Other_Personally_Registered':
            case 'trailer_np':
            case 'camper_np':
                return true;
            default:
                return false;
        }
    };

    const writeValue = (value, changedPath) => {
        onValueChange(value, `${path}.${changedPath}`);
    };

    const isVehiclePrivateOrClassic = () => {
        if (!_.isUndefined(vehicleVM.type_ML.value)) {
            switch (vehicleVM.type_ML.value.code) {
                case 'private_passenger_autos_ml':
                case 'classic_ml':
                    return true;
                default:
                    return false;
            }
        }
        return false;
    };

    //  FIX: Auto-validate when CostNew field becomes visible
    useEffect(() => {
        if (isCostNewRequired && isPrivatePassengerCategory()) {
            const costNewValue = _.get(vehicleVM, 'costNew.value.amount');

            if (!costNewValue || costNewValue <= 0) {
                setValidationMessages({
                    error: [messages.costNewError],
                });

                // mark component invalid so UI reflects immediately
                setComponentValidation(false);
            } else {
                setComponentValidation(true);
            }
        }
    }, [isCostNewRequired, vehicleVM]);

    const isPrivatePassengerCategory = () => {
        if (vehicleVM.category_ML?.value) {
            switch (vehicleVM.category_ML.value.code) {
                case 'private_passenger_light_truck':
                    return true;
                default:
                    return false;
            }
        }
        return false;
    };

    const getVehicleName = () => {
        if (isVehiclePrivateOrClassic(vehicleVM)) {
            return `${_.startCase(_.toLower(vehicleVM.value.getYearMakeModelNameML(translator)))} ${vehicleVM.trim_ML.value}`;
        }
        return vehicleVM.value.getYearMakeModelNameML(translator);
    };

    const getGatewayVehicleDetailsText = () => {
        const vehicleName = getVehicleName();
        return `${vehicleName} ${translator(messages.gatewayVehicleX, { vehicleIndex: (vehicleVM.vehicleNumber_ML.value) })}`
    }
   const [validationMessages, setValidationMessages] = useState({});
   const costNewAmount = _.get(vehicleVM, 'costNew.value.amount');
   // Latched so the field does not unmount while the user is typing
   const [costNewLatched, setCostNewLatched] = useState(() => !(Number(costNewAmount) > 0));
   useEffect(() => {
       if (isCostNewRequired) {
           setCostNewLatched(true);
       }
   }, [isCostNewRequired]);

    const overrideProps = {
        vehicleVinLookupComponent: {
            vehicleVM,
            quoteID,
            isVinDuplicated,
            onValueChange,
            path,
            index,
            visible: isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal() === false,
            onValidate,
            updateVehicleDetails,
            isGpa,
            updateIsValidVin,
            updateInValidVin,
            removeInValidVin,
            isModernProductPersonalAuto,
            onCostNewRequiredChange: (required, hasLookupCostNew) => {
                setIsCostNewRequired(required);
                if (hasLookupCostNew) {
                    setCostNewLatched(false);
                    setValidationMessages({});
                    setComponentValidation(true);
                } else if (required) {
                    setCostNewLatched(true);
                }
            }
        },
        vehiclex: {
            content: translator(messages.vehicleX, { vehicleIndex: (index + 1) })
        },
        yearMake: {
            content: `${vehicleVM.year.value} ${vehicleVM.make.value} `
        },
        modelTrim: {
            content: `${vehicleVM.model.value} ${vehicleVM.trim_ML.value}`
        },
        vehicleFinanceCategory: {
            id: `vehicleFinanceCategory${index}`,
            availableValues: vehicleFinanceCategory,
            onValueChange: updateFinanceCategory,
            value: _.get(vehicleVM.value, 'financeCategory_ML'),
            className: isQnB ? "radioButtonQnB" : ""
        },
        additionalInterestList: {
            model: vehicleVM.additionalInterests,
            submissionVM,
            updateViewModel: updateVehicleDetails,
            onValueChange,
            path,
            initialPotentialContact,
            showValidationErrors,
            onValidate,
            additionalInterestAvailableCandidatesML,
            financeCategory: _.get(vehicleVM.value, 'financeCategory_ML')
        },
        thickhr: {
            visible: lineVisibility()
        },
        namedNonOwnerLabel: {
            visible: vehicleVM.namedNonOwner_ML && vehicleVM.namedNonOwner_ML.value === true
        },
        namedOwnerContainer: {
            visible: !(vehicleVM.namedNonOwner_ML && vehicleVM.namedNonOwner_ML.value === true)
        },
        vehicleModel: {
            visible: !(vehicleVM.namedNonOwner_ML && vehicleVM.namedNonOwner_ML.value === true)
        },
        vehicleCount: {
            visible: !(vehicleVM.namedNonOwner_ML && vehicleVM.namedNonOwner_ML.value === true)
        },
        serialNumber: {
            visible: isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal() === true
        },
        vehicleSummaryContainer: {
            visible: !isGpa
        },
        gatewayVehicleSummaryContainer: {
            visible: isGpa
        },
        gatewayVehicleDetails: {
            content: getGatewayVehicleDetailsText()
        },
        additionalInfoVehicleCostNew: {
            visible: (isCostNewRequired || costNewLatched) && isPrivatePassengerCategory(),
            onBlur: (e) => handleCostNewBlur(e),
            validationMessages: (!costNewAmount || costNewAmount<=0) 
            ? [messages.costNewError]
            : [],
            showValidationErrors : true
        }
    };

    const getQuoteID = () => {
        return submissionVM.quoteID.value;
    };

     const lookupCollision = async () => {
             const vehicleYear = vehicleVM.year.value ? vehicleVM.year.value.toString() : null;
             const req = createLookupCompCollRequest(
                 vehicleYear,
                 _.get(vehicleVM, "makeCode_ML.value"),
                 _.get(vehicleVM, "modelCode_ML.value"),
                 getQuoteID(),
                 _.get(vehicleVM, "type_ML.value.code"),
                 _.get(vehicleVM, "category_ML.value.code"),
                 _.get(vehicleVM, "comprehensiveSymbol_ML.value"),
                 _.get(vehicleVM, "collisionSymbol_ML.value"),
                 _.get(vehicleVM, "costNew.value"),
                 _.get(vehicleVM, "trim_ML.value")
             );
     
             showProgressDialog(platformMessages.loading);
             try {
                 const vehicleInfo = await MLVehicleInfoLookupService.lookUpCollisionCompSymbol(req, authHeader);
     
                 if (vehicleInfo.comprehensiveSymbol_ML && vehicleInfo.collisionSymbol_ML) {
                     _.set(vehicleVM, "comprehensiveSymbol_ML.value", vehicleInfo.comprehensiveSymbol_ML);
                     _.set(vehicleVM, "collisionSymbol_ML.value", vehicleInfo.collisionSymbol_ML);
                     _.set(vehicleVM, "hasSymbolsFilledByCostNew_Ext.value", true);
                 }
             } finally {
                 hideProgressDialog();
             }
         };

    const handleCostNewBlur = async (e) => {
        try{
            const costNewValue= Number(e.target.value) || 0;

            if (_.isUndefined(costNewValue) || _.isNull(costNewValue) || costNewValue <= 0) {
                    _.set(vehicleVM, 'costNew.value.amount', undefined);
             setValidationMessages({
                error: [messages.costNewError],
            });

            } else if (costNewValue > 0) {
                    _.set(vehicleVM, 'costNew.value.amount', costNewValue);
                   setValidationMessages({});
                   lookupCollision();
                   updateVehicleDetails();
                }

        } catch (error) {
            console.error("lookUpSymbol failed", error)
        }
    };

    const resolvers = {
        resolveComponentMap: {
            namednonownertooltipcomponent: NamedNonOwnerTooltipComponent
        },
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            overrideProps={overrideProps}
            model={vehicleVM}
            onValidationChange={setComponentValidation}
            onValueChange={writeValue}
            showErrors={showValidationErrors}
        />
    );
}

VehicleInfoComponent.propTypes = {
    value: PropTypes.shape({}).isRequired,
    id: PropTypes.string.isRequired,
    isVinDuplicated: PropTypes.func.isRequired,
    onValueChange: PropTypes.func.isRequired,
    path: PropTypes.string.isRequired,
    quoteID: PropTypes.string,
    index: PropTypes.number.isRequired,
    updateVehicleDetails: PropTypes.func.isRequired,
    initialPotentialContact: PropTypes.shape([]).isRequired,
    showValidationErrors: PropTypes.bool.isRequired,
    onValidate: PropTypes.func.isRequired,
    isGpa: PropTypes.bool,
    updateIsValidVin: PropTypes.func,
    submissionVM: PropTypes.shape({}).isRequired,
    updateInValidVin: PropTypes.func,
    removeInValidVin: PropTypes.func
};

VehicleInfoComponent.defaultProps = {
    quoteID: undefined,
    isGpa: false,
    updateIsValidVin: undefined
};

export default VehicleInfoComponent;
